import { useEffect, useRef } from 'react';

export const FLOW_COLORS = { income: '#168978', expense: '#B94B5A' };
const invisibleColor = (color) => !color || color === 'none' || color === 'transparent'
    || /rgba\([^)]*,\s*0(?:\.0+)?\s*\)/.test(color);

// Presentation attributes provide the baseline; animation is a removable effect.
// No dash or opacity from the animation is committed into inline styles.
export const useSeriesPaint = (containerRef, { geometry, layout, accountId, periodKey, visibleSeries, valuesVisible, debug }) => {
    const lastReport = useRef('');
    const signature = JSON.stringify([accountId, periodKey, visibleSeries, geometry.paths, geometry.valid]);
    const latest = useRef(null);
    latest.current = { geometry, layout, accountId, periodKey, valuesVisible, debug };

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return undefined;
        let disposed = false;
        const animations = [];
        const motion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
        const cancelAnimations = () => {
            for (const animation of animations.splice(0)) animation.cancel();
        };
        const inspect = () => {
            if (disposed) return;
            const current = latest.current;
            const { geometry: graph } = current;
            if (!graph.pointCount) return;
            const problems = [];
            if (!graph.valid) problems.push({ reason: 'INVALID_GEOMETRY', series: graph.series });
            const box = container.getBoundingClientRect();
            const measurable = box.width > 0 && box.height > 0;
            if (graph.valid && measurable) {
                const tokens = getComputedStyle(container);
                container.querySelectorAll('path[data-flow-series]').forEach((path) => {
                    // Drop a former recovery before checking current CSS/theme.
                    path.style.removeProperty('stroke');
                    path.style.removeProperty('stroke-opacity');
                    path.style.removeProperty('opacity');
                    const style = getComputedStyle(path);
                    const key = path.getAttribute('data-flow-series');
                    const badStroke = invisibleColor(style.stroke);
                    const badOpacity = style.opacity === '0' || style.strokeOpacity === '0';
                    if (badStroke || badOpacity) {
                        const computed = { stroke: style.stroke, opacity: style.opacity, strokeOpacity: style.strokeOpacity,
                            strokeWidth: style.strokeWidth, clipPath: style.clipPath, mask: style.mask };
                        const token = tokens.getPropertyValue('--nf-' + key).trim();
                        const supported = token && window.CSS?.supports?.('color', token) && !invisibleColor(token);
                        if (badStroke) path.style.stroke = supported ? token : FLOW_COLORS[key];
                        if (badOpacity) { path.style.opacity = '1'; path.style.strokeOpacity = '1'; }
                        problems.push({ reason: 'INVISIBLE_PAINT', key, path: path.getAttribute('d'), computed });
                    }
                });
            }
            if (process.env.NODE_ENV === 'development' && current.debug && current.valuesVisible && problems.length) {
                const report = { accountId: current.accountId, periodKey: current.periodKey,
                    pointCount: graph.pointCount, width: box.width, height: box.height,
                    xDomain: graph.xDomain, yDomain: graph.yDomain, paths: graph.paths,
                    hasNaN: graph.hasNaN, problems };
                const key = JSON.stringify(report);
                if (key !== lastReport.current) console.debug('[FinancialFlowChart inconsistency]', report);
                lastReport.current = key;
            } else if (!problems.length) lastReport.current = '';
        };
        const resume = () => { cancelAnimations(); inspect(); };

        inspect();
        const current = latest.current;
        if (current.layout.ready && current.geometry.valid && !motion?.matches && !document.hidden) {
            container.querySelectorAll('path[data-flow-series]').forEach((path) => {
                if (typeof path.animate !== 'function' || typeof path.getTotalLength !== 'function') return;
                try {
                    const length = path.getTotalLength();
                    if (!Number.isFinite(length) || length <= 0) return;
                    const dash = `${length} ${length}`;
                    const animation = path.animate([
                        { strokeDasharray: dash, strokeDashoffset: String(length) },
                        { strokeDasharray: dash, strokeDashoffset: '0' },
                    ], { duration: 720, delay: path.dataset.flowSeries === 'expense' ? 80 : 0,
                        easing: 'cubic-bezier(.22, 1, .36, 1)', fill: 'backwards' });
                    animation.onfinish = () => animation.cancel();
                    animations.push(animation);
                } catch { /* Unsupported SVG animation leaves the visible base stroke intact. */ }
            });
            container.querySelectorAll('circle[data-flow-series]').forEach((point) => {
                if (typeof point.animate !== 'function') return;
                try {
                    const animation = point.animate([{ opacity: 0 }, { opacity: 1 }], {
                        duration: 220, delay: 560, easing: 'ease-out', fill: 'backwards',
                    });
                    animation.onfinish = () => animation.cancel();
                    animations.push(animation);
                } catch { /* Keep the visible presentation attributes. */ }
            });
        }
        motion?.addEventListener?.('change', resume);
        // Safari versions exposing only the legacy MediaQueryList listener API.
        if (!motion?.addEventListener) motion?.addListener?.(resume);
        window.addEventListener('pageshow', resume);
        window.addEventListener('pagehide', cancelAnimations);
        document.addEventListener('visibilitychange', resume);
        let observer;
        if (typeof MutationObserver === 'function') {
            observer = new MutationObserver(resume);
            observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'data-theme'] });
        }
        return () => {
            disposed = true;
            cancelAnimations();
            observer?.disconnect();
            motion?.removeEventListener?.('change', resume);
            if (!motion?.removeEventListener) motion?.removeListener?.(resume);
            window.removeEventListener('pageshow', resume);
            window.removeEventListener('pagehide', cancelAnimations);
            document.removeEventListener('visibilitychange', resume);
        };
    }, [containerRef, signature, layout.ready]);
};
